// Lightweight, deterministic rule checks for General + Objective tabs
// Returns an array of flags: { id, severity: 'info'|'warning'|'error', message }

const hasText = (s) => typeof s === 'string' && s.trim().length > 0;
const parseNum = (s) => {
  if (!hasText(s)) return null;
  const n = parseFloat(String(s).replace(/[^-0-9.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

function coverRowFlags(scope, row) {
  const flags = [];
  if (!row) return flags;
  const anyValues = hasText(row?.unil?.h) || hasText(row?.unil?.v) || hasText(row?.alt?.h) || hasText(row?.alt?.v);
  if (row?.ortho && anyValues) {
    flags.push({ id: `${scope}-ortho-conflict`, severity: 'warning', message: `${scope}: Ortho is checked but measurements are present.` });
  }

  if (hasText(row?.unil?.h) && !hasText(row?.unil?.hDir)) {
    flags.push({ id: `${scope}-unil-h-dir-missing`, severity: 'warning', message: `${scope}: Unil H value present without direction (BO/BI).` });
  }
  if (hasText(row?.unil?.v) && !hasText(row?.unil?.vDir)) {
    flags.push({ id: `${scope}-unil-v-dir-missing`, severity: 'warning', message: `${scope}: Unil V value present without direction (BD/BU).` });
  }
  if (hasText(row?.alt?.h) && !hasText(row?.alt?.hDir)) {
    flags.push({ id: `${scope}-alt-h-dir-missing`, severity: 'warning', message: `${scope}: Alt H value present without direction (BO/BI).` });
  }
  if (hasText(row?.alt?.v) && !hasText(row?.alt?.vDir)) {
    flags.push({ id: `${scope}-alt-v-dir-missing`, severity: 'warning', message: `${scope}: Alt V value present without direction (BD/BU).` });
  }
  return flags;
}

export function evaluateObjectiveRules(objective = {}, general = {}, opts = {}) {
  const flags = [];

  // Stereopsis completeness
  const stereo = objective?.stereopsis || {};
  const method = stereo?.method || '';
  const d = stereo?.d || '';
  const n = stereo?.n || '';
  if (hasText(method) && !hasText(d) && !hasText(n)) {
    flags.push({ id: 'stereo-missing-values', severity: 'info', message: 'Stereopsis method selected but D and N are empty.' });
  }
  if ((hasText(d) || hasText(n)) && !hasText(method)) {
    flags.push({ id: 'stereo-missing-method', severity: 'info', message: 'Stereopsis values entered but method is not selected.' });
  }

  // Cover test consistency
  flags.push(...coverRowFlags('Cover Test (Dist)', objective?.coverTest?.dist));
  flags.push(...coverRowFlags('Cover Test (Near)', objective?.coverTest?.near));

  // Color deficiency info
  const color = objective?.color || {};
  if (color?.od === 'colour-deficiency' || color?.os === 'colour-deficiency') {
    flags.push({ id: 'color-deficiency', severity: 'info', message: 'Color deficiency recorded (OD/OS). Consider counseling and documentation.' });
  }

  // NPC heuristic
  const npcNum = parseNum(objective?.npc);
  if (npcNum != null && npcNum > 10) {
    flags.push({ id: 'npc-high', severity: 'info', message: `NPC (${npcNum}) may be high; correlate with symptoms (convergence).` });
  }

  // Amsler keyword scan
  const amsler = objective?.tests?.amsler || {};
  const amslerText = `${amsler.od || ''} ${amsler.os || ''}`.toLowerCase();
  if (/wavy|distort|scotoma|missing|central/.test(amslerText)) {
    flags.push({ id: 'amsler-abnormal', severity: 'warning', message: 'Amsler notes may indicate macular changes; consider further evaluation.' });
  }

  // Fields keyword scan
  const fields = objective?.tests?.fields || {};
  const fieldsText = `${fields.od || ''} ${fields.os || ''}`.toLowerCase();
  if (/constrict|hemian|quadrantan|defect|nasal step/.test(fieldsText)) {
    flags.push({ id: 'fields-abnormal', severity: 'warning', message: 'Visual fields notes may indicate defects; review and consider follow-up.' });
  }

  return flags;
}

export function buildPatientSummary(general = {}, objective = {}) {
  const parts = [];
  if (hasText(general?.complaint)) parts.push(`Visit reason: ${general.complaint}.`);
  // Cover test quick statement
  const dist = objective?.coverTest?.dist; const near = objective?.coverTest?.near;
  const distOrtho = dist?.ortho && !hasText(dist?.unil?.h) && !hasText(dist?.unil?.v) && !hasText(dist?.alt?.h) && !hasText(dist?.alt?.v);
  const nearOrtho = near?.ortho && !hasText(near?.unil?.h) && !hasText(near?.unil?.v) && !hasText(near?.alt?.h) && !hasText(near?.alt?.v);
  if (distOrtho && nearOrtho) parts.push('Cover test: Ortho at distance and near.');
  else if (distOrtho) parts.push('Cover test: Ortho at distance.');
  else if (nearOrtho) parts.push('Cover test: Ortho at near.');

  // Stereopsis
  const method = objective?.stereopsis?.method;
  const d = objective?.stereopsis?.d; const n = objective?.stereopsis?.n;
  if (hasText(method) && (hasText(d) || hasText(n))) {
    parts.push(`Stereopsis (${method}): ${hasText(d) ? `D ${d}` : ''}${hasText(d) && hasText(n) ? ', ' : ''}${hasText(n) ? `N ${n}` : ''}.`);
  }

  // Color
  if (objective?.color?.od === 'colour-deficiency' || objective?.color?.os === 'colour-deficiency') {
    parts.push('Color vision differences were noted.');
  }

  // Optional free text cues
  if (hasText(objective?.notes)) parts.push(`Notes: ${objective.notes}`);

  return parts.join(' ');
}
